import { layoutTwoLevel } from './treemap';

/**
 * Render the portfolio heatmap to a shareable PNG, in the browser.
 *
 * Deliberately NOT a server route like /chart/{TICKER}.png. That one is public
 * market data keyed by a ticker, so a public URL is harmless. A portfolio
 * heatmap is the user's own book, and a /heatmap/{user}.png endpoint would be a
 * permanent public URL serving someone's holdings to anyone who guessed it.
 * Rendering here produces a file to attach to a post and creates no endpoint.
 *
 * WHAT THIS IMAGE DOES NOT CONTAIN: any dollar amount. Tiles carry the ticker
 * and a percentage; size carries relative weight. The on-screen footer shows
 * "$X total" and is excluded on purpose — he approved sharing what he owns and
 * how concentrated he is, not how much he has.
 */

const W = 1200;
const H = 675;           // 16:9, the shape X renders without cropping
const PAD = 40;
const HEADER = 96;
const FOOTER = 54;

const BG = '#0C1220';
const TEXT = '#F1F5F9';
const MUTED = '#94A3B8';
const ACCENT = '#00D4AA';

function roundRect(ctx, x, y, w, h, r) {
  const rad = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
}

/**
 * @param {Array}  groups    [{ name, children:[{name,size,value,pct}] }]
 * @param {Object} opts      { modeLabel, colorFor, mode, positions }
 * @returns {Promise<Blob>}
 */
export async function renderHeatmapPng(groups, opts = {}) {
  const { modeLabel = '', colorFor, mode = 'total', positions = 0 } = opts;

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, H);

  // ── header ────────────────────────────────────────────────────────────────
  ctx.fillStyle = TEXT;
  ctx.font = '700 30px -apple-system, Segoe UI, Roboto, sans-serif';
  ctx.textBaseline = 'top';
  ctx.fillText('My portfolio', PAD, PAD - 4);

  ctx.font = '400 17px -apple-system, Segoe UI, Roboto, sans-serif';
  ctx.fillStyle = MUTED;
  const sub = `${positions} position${positions === 1 ? '' : 's'} · box size is relative weight · colour is ${modeLabel}`;
  ctx.fillText(sub, PAD, PAD + 36);

  // Brand, right-aligned on the title line.
  ctx.textAlign = 'right';
  ctx.font = '700 22px -apple-system, Segoe UI, Roboto, sans-serif';
  ctx.fillStyle = TEXT;
  ctx.fillText('Alpha', W - PAD - 54, PAD);
  ctx.fillStyle = ACCENT;
  ctx.fillText('Grade', W - PAD, PAD);
  ctx.textAlign = 'left';

  // ── treemap ───────────────────────────────────────────────────────────────
  const plotW = W - PAD * 2;
  const plotH = H - HEADER - FOOTER - PAD;
  const cells = layoutTwoLevel(groups, plotW, plotH);
  const offX = PAD;
  const offY = HEADER;

  cells.forEach((c) => {
    const x = offX + c.x;
    const y = offY + c.y;
    const w = Math.max(c.w - 3, 0);
    const h = Math.max(c.h - 3, 0);
    if (w < 2 || h < 2) return;

    ctx.fillStyle = colorFor ? colorFor(c.pct, mode) : '#475569';
    roundRect(ctx, x, y, w, h, 4);
    ctx.fill();

    // Dark text on every shade in the ramp, so contrast never flips.
    ctx.fillStyle = '#04121C';
    ctx.textAlign = 'center';

    const showTicker = w > 52 && h > 30;
    const showPct = w > 68 && h > 50;
    if (showTicker) {
      ctx.font = `700 ${Math.min(19, Math.max(12, w / 5.5))}px ui-monospace, Menlo, Consolas, monospace`;
      ctx.textBaseline = showPct ? 'bottom' : 'middle';
      ctx.fillText(c.name, x + w / 2, showPct ? y + h / 2 - 1 : y + h / 2);
    }
    if (showPct && c.pct != null) {
      ctx.font = '600 14px -apple-system, Segoe UI, Roboto, sans-serif';
      ctx.textBaseline = 'top';
      const s = `${c.pct >= 0 ? '+' : ''}${c.pct.toFixed(1)}%`;
      ctx.fillText(s, x + w / 2, y + h / 2 + 3);
    }
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
  });

  // ── footer ────────────────────────────────────────────────────────────────
  // No totals here, by design. See the note at the top of this file.
  ctx.font = '400 14px -apple-system, Segoe UI, Roboto, sans-serif';
  ctx.fillStyle = MUTED;
  const d = new Date().toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
  ctx.fillText(`${d} · alphagrade.io`, PAD, H - FOOTER + 12);

  ctx.textAlign = 'right';
  ctx.fillText('Research and education, not financial advice.', W - PAD, H - FOOTER + 12);
  ctx.textAlign = 'left';

  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}

/** Save the blob, and copy it to the clipboard when the browser allows. */
export async function shareHeatmapPng(blob) {
  let copied = false;
  try {
    // Only works on a secure origin, and Firefox still does not support it.
    if (navigator.clipboard && window.ClipboardItem) {
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
      copied = true;
    }
  } catch {
    // Non-fatal: the download below is the reliable path.
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `alphagrade-portfolio-${new Date().toISOString().slice(0, 10)}.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return copied;
}
